import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { PhoneIcon } from '@rsos/assets/icons';
import { Box, Text } from '@rsos/components/capstone/base';
import { CALL_OUT_OF_JURISDICTION } from '@rsos/constants/collaborator';
import { useGetSelectedCollaborator } from '@rsos/reduxDataStore/src/features/emergencyCalls/helpers';
import { useSelectedTextFrom911Conversation } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import { TF911_CONVERSATION_ENDED } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import useDisplayPhoneNumber from '../../../../hooks/useDisplayPhoneNumber';
import useHasAccessToUniteEventPane from '../../../../hooks/useHasAccessToUniteEventPane';
import { trackTogglingTF911ChatBox } from '../textFrom911Trackings';
import { HeaderContainer, RedCircle } from './TF911.styles';
import TF911ChatHeaderButtons from './TF911ChatHeaderButtons';
import TF911ParticipantsPopover from './TF911ParticipantsPopover';
import { TF911_CHAT_HEADER_HEIGHT } from './constants';

/**
 * Displays a chat header above the chat box.
 * @param {Array} callChatParticipants - list of chat participants for 911 calls
 * @param {Boolean} isChatBoxVisible - Flag for expanding/collapsing the chat
 * @param {Boolean} isForOverview - Flag for checking if the component is used for the overview tab
 * @param {Function} onActionButtonClick - Onclick handler for toggling the
 * isTextFrom911ChatBoxVisible flag
 * @param {Function} onClickShowAccordion - Onclick handler for toggling the accordion for preconfigured messages
 * @param {Function} onStartConversation - Onclick handler for starting the conversation
 * @param {Boolean} showStartChat - Flag for showing the Start Chat button
 * @param {Boolean} showPreconfiguredMessages - Flag for showing preconfigured messages to start the chat with
 * @param {Boolean} isOutboundSMSConversation - Flag for if conversation is from OutboundSMS
 */
const TF911ChatHeader = ({
  isChatBoxVisible,
  isForOverview = false,
  onActionButtonClick,
  onClickShowAccordion,
  onStartConversation,
  showStartChat,
  showPreconfiguredMessages,
  isOutboundSMSConversation,
  gpxFeature,
}) => {
  const hasAccessToUniteEventPane = useHasAccessToUniteEventPane();

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const collaborator = useGetSelectedCollaborator();
  const isOutOfJurisdiction = collaborator.type === CALL_OUT_OF_JURISDICTION;

  const conversationChatHeader = useMemo(() => {
    if (collaborator.type) {
      if (isOutOfJurisdiction) {
        return 'Citizen Live Chat - Read Only';
      }
      return 'Citizen Live Chat';
    }
    return 'Live Chat';
  }, [collaborator.type, isOutOfJurisdiction]);

  const displayPhoneNumber = useDisplayPhoneNumber(selectedCallerID);

  const conversation = useSelectedTextFrom911Conversation(selectedCallerID);

  const isFetchingMetadata = useMemo(
    () => conversation?.loading?.getConversationMetadata,
    [conversation],
  );

  const isCreatingConversation = useMemo(
    () => conversation?.loading?.createConversation,
    [conversation],
  );

  const isConversationEnded = useMemo(
    () => conversation?.data?.custom?.STATUS === TF911_CONVERSATION_ENDED,
    [conversation],
  );

  const headerMaxHeight = useMemo(() => {
    if (showStartChat) {
      return '100%';
    }
    return `${TF911_CHAT_HEADER_HEIGHT}px`;
  }, [showStartChat]);

  const headerHeight = useMemo(() => {
    if (showPreconfiguredMessages) {
      return '100%';
    }
    return `auto`;
  }, [showPreconfiguredMessages]);

  const handleActionButtonClick = useCallback(() => {
    onActionButtonClick();
    trackTogglingTF911ChatBox({
      isExpanded: !isChatBoxVisible,
      feature: gpxFeature,
      fromOverviewTab: isForOverview,
      isUniteEventPaneEnabled: hasAccessToUniteEventPane,
      conversationId: conversation?.data?.conversation_id,
    });
  }, [
    onActionButtonClick,
    isChatBoxVisible,
    gpxFeature,
    isForOverview,
    hasAccessToUniteEventPane,
    conversation,
  ]);

  return (
    <HeaderContainer
      data-name="text-from-911-chat-header"
      height={headerHeight}
      maxHeight={headerMaxHeight}
      pb={isOutboundSMSConversation && showStartChat && '12px'}
      pt={isForOverview ? '16px' : '12px'}
    >
      <Box
        horizontal
        justify="space-between"
        px={isForOverview ? '16px' : '12px'}
        width="100%"
      >
        <Box>
          <Text inline fontWeight="bold">
            <Text color="primaryText" size="normal">
              {!isFetchingMetadata &&
                conversation?.data?.conversation_id &&
                !isConversationEnded && <RedCircle />}
              {!isFetchingMetadata &&
                (!isConversationEnded ? conversationChatHeader : 'Closed Chat')}
            </Text>
            <Box horizontal>
              {!hasAccessToUniteEventPane && (
                <Text color="secondaryText">
                  <PhoneIcon
                    width={10}
                    height={12}
                    viewboxWidth={11}
                    viewboxHeight={15}
                  />
                </Text>
              )}
              <Text
                color="secondaryText"
                fontWeight={hasAccessToUniteEventPane ? 'regular' : 'inherit'}
                size={hasAccessToUniteEventPane ? 'xsmall' : 'small'}
              >
                {displayPhoneNumber}
              </Text>
            </Box>
          </Text>
        </Box>

        <TF911ParticipantsPopover
          showStartChat={showStartChat}
          isChatBoxVisible={isChatBoxVisible}
          onActionButtonClick={handleActionButtonClick}
          isConversationEnded={isConversationEnded}
        />
      </Box>
      {!isOutOfJurisdiction && (
        <TF911ChatHeaderButtons
          isFetchingMetadata={isFetchingMetadata}
          showStartChat={showStartChat}
          isCreatingConversation={isCreatingConversation}
          hasAccessToUniteEventPane={hasAccessToUniteEventPane}
          isForOverview={isForOverview}
          onClickShowAccordion={onClickShowAccordion}
          onStartConversation={onStartConversation}
          selectedCallerID={selectedCallerID}
          showPreconfiguredMessages={showPreconfiguredMessages}
          gpxFeature={gpxFeature}
        />
      )}
    </HeaderContainer>
  );
};

TF911ChatHeader.propTypes = {
  callParticipants: PropTypes.array,
  isChatBoxVisible: PropTypes.bool,
  isForOverview: PropTypes.bool,
  onActionButtonClick: PropTypes.func,
  onClickShowAccordion: PropTypes.func,
  onEndConversation: PropTypes.func,
  onStartConversation: PropTypes.func,
  showStartChat: PropTypes.bool,
  showPreconfiguredMessages: PropTypes.bool,
  toggleChatBoxVisibility: PropTypes.func,
};

export default TF911ChatHeader;
